import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import getSymbolFromCurrency from 'currency-symbol-map';

import styled, { useTheme } from 'styled-components';

import { chartMessages } from './messages';
import { parseMonthIntoDate } from './utils';

import Spinner from '../../../legacy/main/components/UI/spinner';
import ChangePeriodControls from './ChangePeriodControls';
import DataTypeFilters, { GRAPH_TYPES } from './DataTypeFilters';
import BarChart from './BarChart';
import Footer from './Footer';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router';

const Container = styled.div`
    width: 100%;
    height: 100%;
    padding: 15px;
    text-align: center;
    color: ${({ theme }) => theme.text};
    font-family: ${({ theme }) => theme.font.title};
    font-size: 12px;
`;
const TooltipBreakdown = styled.div`
    margin-left: 10px;
`;

const STACK_ID = 'BusinessChangeStackId';

const BusinessChange = ({
    isFetching,
    currency,
    data = null,
    range,
    onChangeBookingDateRange,
    dashboardVersion = false,
}) => {
    const intl = useIntl();
    const theme = useTheme();
    const navigate = useNavigate();

    const [dataType, setDataType] = useState(dashboardVersion ? GRAPH_TYPES.REVENUE : GRAPH_TYPES.ROOMS);

    const onClickBar = (date) => {
        navigate(dashboardVersion ? '/app/dashboard' : '/app/monthly', {
            state: { selectedDate: parseMonthIntoDate(date) },
        });
    };

    const onSelectRange = (value) => {
        onChangeBookingDateRange(value);
    };

    const onChangeDataType = (selectedDataType) => {
        setDataType(selectedDataType);
    };

    const renderBarChart = () => {
        const roomsSettings = {
            accessors: {
                guaranteed: (row) => row.gain.guaranteed.rooms,
                nonGuaranteed: (row) => row.gain.nonGuaranteed.rooms,
                loss: (row) => row.loss.rooms,
            },
            formatter: intl.formatNumber,
        };
        const revenueSettings = {
            accessors: {
                guaranteed: (row) => row.gain.guaranteed.revenue.accommodation,
                nonGuaranteed: (row) => row.gain.nonGuaranteed.revenue.accommodation,
                loss: (row) => row.loss.revenue.accommodation,
            },
            formatter: (revenue) => {
                const currencySymbol = getSymbolFromCurrency(currency);
                const currencyValue = intl.formatNumber(revenue / 100, {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0,
                });

                return `${currencySymbol}${currencyValue}`;
            },
        };

        const bookingBreakdown = (row) => ({
            [intl.formatMessage(chartMessages.guaranteedGainLabel)]: row?.payload?.gain?.guaranteed?.bookings,
            [intl.formatMessage(chartMessages.nonGuaranteedGainLabel)]: row?.payload?.gain?.nonGuaranteed?.bookings,
            [intl.formatMessage(chartMessages.lossLabel)]: row?.payload?.loss?.bookings,
        });

        const settings = dataType === GRAPH_TYPES.ROOMS ? roomsSettings : revenueSettings;
        const graphSettings = {
            bars: [
                {
                    type: 'guaranteed',
                    label: intl.formatMessage(chartMessages.guaranteedGainLabel),
                    valueAccessor: settings.accessors.guaranteed,
                    colour: theme.chartsPalette.cyan,
                    stackId: STACK_ID,
                },
                {
                    type: 'nonGuaranteed',
                    label: intl.formatMessage(chartMessages.nonGuaranteedGainLabel),
                    valueAccessor: settings.accessors.nonGuaranteed,
                    colour: theme.chartsPalette.yellow,
                    stackId: STACK_ID,
                },
                {
                    type: 'loss',
                    label: intl.formatMessage(chartMessages.lossLabel),
                    valueAccessor: (row) => -settings.accessors.loss(row),
                    colour: theme.chartsPalette.red,
                    stackId: STACK_ID,
                },
            ],
            xAxisValueAccessor: 'stayDate',
            xAxisTickFormatter: (date) =>
                intl.formatDate(parseMonthIntoDate(date), {
                    year: '2-digit',
                    month: 'short',
                }),
            yAxisTickFormatter: settings.formatter,
            tooltipLabelFormatter: (date) =>
                intl.formatDate(parseMonthIntoDate(date), {
                    year: 'numeric',
                    month: 'long',
                }),
            tooltipFormatter: (value, label, row) => (
                <>
                    {intl.formatMessage(chartMessages.tooltipLabel, {
                        label,
                        value: settings.formatter(Math.abs(value)),
                    })}
                    {bookingBreakdown(row)[label] && (
                        <>
                            {Object.keys(bookingBreakdown(row)[label]).map((key) => {
                                if (!isEmpty(bookingBreakdown(row)[label][key])) {
                                    return (
                                        <TooltipBreakdown>
                                            {intl.formatMessage(chartMessages[`tooltipBreakdown_${key}`], {
                                                value: bookingBreakdown(row)[label][key].length,
                                            })}
                                        </TooltipBreakdown>
                                    );
                                }
                                return <></>;
                            })}
                        </>
                    )}
                </>
            ),
        };

        return <BarChart data={data} onClickBar={onClickBar} dashboardVersion={dashboardVersion} {...graphSettings} />;
    };

    const content = isFetching ? (
        <Spinner />
    ) : (
        <>
            {renderBarChart()}
            {!dashboardVersion && <Footer data={data} currency={currency} />}
        </>
    );
    return (
        <Container>
            {!dashboardVersion && (
                <>
                    <ChangePeriodControls defaultRange={range} onChangeRange={onSelectRange} disabled={isFetching} />
                    <DataTypeFilters dataType={dataType} onChangeDataType={onChangeDataType} />
                </>
            )}
            {content}
        </Container>
    );
};

export default BusinessChange;
